'use client';

/**
 * 画布登录页（2026-10-08 左轮播改版）：根路由 `/` 未登录态与 `/login` 路由共用。
 * 布局：lg 左侧全出血 AIGC 品牌图轮播（LoginCarousel）+ 右侧登录表单（LoginFormPanel 共享组件）。
 *
 * 2026-10-10 theme 化：
 * - theme="canvas"（默认）：原深灰 #313131 + purple 光晕 + LoginFormPanel page 变体——画布未登录首页专用。
 * - theme="dialog"：与首页弹窗登录完全同款（#1c1c1c 卡片底 + emerald 皮肤 + dialog 变体表单）。
 *   站内跳转落点 /login（复刻/点赞等未登录引导）用此主题，保证「跳转登录页」与「弹窗登录」视觉一致。
 */

import { LoginFormPanel } from '@/components/auth/LoginFormPanel';
import { LoginCarousel } from '@/components/auth/LoginCarousel';
import { trackEvent } from '@/lib/utmClient';

type LoginTheme = 'canvas' | 'dialog';

/** 站内页面跳转前缀（与 home/page.tsx SITE_PREFIX 同模式）：prod 根域直达 /home，staging 保留子路径 */
const SITE_PREFIX = process.env.NEXT_PUBLIC_BASE_PATH === '/aigc' ? '' : process.env.NEXT_PUBLIC_BASE_PATH || '';

/** 返回首页入口（2026-10-20）：两处全屏登录页（画布未登录 / /login 路由）右上角固定按钮；
 *  点击先 fire-and-forget 打点（事件 /event/canvas-back-home，后台「行为事件」卡可查）再跳转 */
function BackHomeButton() {
  const goHome = () => {
    trackEvent('canvas-back-home'); // sendBeacon 在页面卸载时仍可靠送达
    window.location.href = `${SITE_PREFIX}/home`;
  };
  return (
    <button
      type="button"
      onClick={goHome}
      className="fixed top-4 right-4 z-50 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur text-[13px] font-medium text-slate-200 transition-colors hover:bg-white/20 hover:text-white cursor-pointer"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h5v-6h4v6h5V9.5" />
      </svg>
      返回首页
    </button>
  );
}

export function LoginPage({ theme = 'canvas' }: { theme?: LoginTheme }) {
  if (theme === 'dialog') {
    return (
      <div className="min-h-screen w-screen flex bg-[#1c1c1c] relative overflow-hidden">
        <BackHomeButton />
        {/* 左侧轮播区：与弹窗同款素材 */}
        <div className="hidden sm:block w-[52%] shrink-0 relative overflow-hidden">
          <LoginCarousel variant="dialog" />
        </div>

        {/* 右侧表单区：弹窗同款 emerald 皮肤 */}
        <div className="relative flex-1 flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/3 left-1/3 w-96 h-96 rounded-full bg-emerald-500/10 blur-[120px]" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-teal-500/5 blur-[100px]" />
          </div>

          <div className="relative z-10 w-full max-w-[420px] px-8 py-10">
            <LoginFormPanel variant="dialog" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-screen flex bg-[#313131] relative overflow-hidden">
      <BackHomeButton />
      {/* 左侧轮播区（lg 及以上显示）：全出血 AIGC 品牌图轮播（2026-10-08） */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <LoginCarousel variant="page" />
      </div>

      {/* 右侧表单区 */}
      <div className="relative w-full lg:w-[480px] xl:w-[520px] shrink-0 flex items-center justify-center overflow-hidden">
      {/* Background gradient effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-purple-600/10 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-pink-600/10 blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-blue-600/5 blur-[150px]" />
      </div>

      {/* Grid background */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative z-10 w-full max-w-md px-6 py-8">
        <LoginFormPanel />

        {/* Feature badges */}
        <div className="flex items-center justify-center gap-6 mt-6 text-[11px] text-slate-600">
          <span className="flex items-center gap-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
            多模型 AI 生成
          </span>
          <span className="flex items-center gap-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-pink-500" />
            可视化工作流
          </span>
          <span className="flex items-center gap-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
            数据隔离
          </span>
        </div>
      </div>
      </div>
    </div>
  );
}
